/**
 * A aba "Consulta" no card da oportunidade (#811, clínica; #1011, o SISAC).
 *
 * ── Só com a clínica ligada ──────────────────────────────────────────────
 *
 * Quem decide se a aba existe é o card: ela só aparece com a funcionalidade
 * "clinica" ligada. Fora dela as rotas respondem 404.
 *
 * ── As telas e as rotas ──────────────────────────────────────────────────
 *
 * As telas vieram do protótipo do Claude Design e não falam com a API
 * (`lista-de-consultas`, `nova-consulta`, `editar-consulta`). Este arquivo as
 * liga: lê a consulta, salva a edição, e entrega a "Nova consulta" ao
 * `useNovaConsulta`, que fala com o SISAC.
 *
 * ── Uma lista com um item ────────────────────────────────────────────────
 *
 * O protótipo mostra as consultas em lista, e o CRM guarda uma consulta por
 * oportunidade: a lista tem zero ou um item. O paciente pode marcar mais de uma
 * consulta, e "Nova consulta" fica liberada mesmo com uma já marcada: a nova
 * toma o lugar da anterior no CRM, e a anterior continua marcada no SISAC.
 *
 * ── Duas mãos, cada uma no seu campo ─────────────────────────────────────
 *
 * Quem edita a oportunidade escolhe médico e especialidade. Os códigos do
 * paciente e da agenda, e o horário marcado, quem grava é a integração, ao
 * agendar. Depois de marcada, "Editar" muda só o CRM, e a tela diz isso.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type ConsultaDaOportunidade } from '@cliqcrm/contracts';
import { useApi, useConsultaDaOportunidade, useOpcoesDaConsulta } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { usePreferencias } from '@/shared/sessao/contexto';
import { pacienteNoSisacLigado, telefoneParaOSisac } from '@/entities/saude/paciente';
import { PacienteNoSisac, type ContatoDoPaciente } from '@/features/saude/vincular-paciente';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import {
  ID_DA_CONSULTA,
  consultaMarcada,
  consultasNaLista,
  doCampo,
  opcoesComAtual,
  paraCampo,
} from '../lib/consulta-da-oportunidade';
import type { ConsultaNaLista, EstadoDoEnvio } from '../lib/consultas-na-tela';
import { avisoDaEscrita } from '../lib/consultas-na-tela';
import { DIAS_DE_VAGAS, operacoesDaAgenda, type OperacoesDaAgenda } from '../lib/marcar-no-sisac';
import { useNovaConsulta } from '../model/usar-nova-consulta';
import { useCancelamento } from '../model/usar-cancelamento';
import { CancelarConsulta } from './cancelar-consulta';
import { EditarConsulta, type EdicaoDaConsulta } from './editar-consulta';
import { ListaDeConsultas } from './lista-de-consultas';
import { NovaConsulta, type EscolhaDoMedico } from './nova-consulta';

type Modo = 'lista' | 'editar' | 'nova' | 'cancelar';

export function AbaDaConsulta({
  oportunidadeId,
  podeEditar,
  contato = null,
  comecarAgendando = false,
  comecarEm,
  forma,
  noComeco,
  aoAgendar,
  aoCancelarAgendamento,
  aoConcluir,
}: {
  oportunidadeId: string;
  podeEditar: boolean;
  /** O contato da oportunidade, quando quem monta a aba o tem: a busca do paciente usa. */
  contato?: ContatoDoPaciente | null;
  /** Abre direto na "Nova consulta": o "Agendar consulta" do Atendimento. */
  comecarAgendando?: boolean;
  /**
   * Abre direto no "Editar" ou no "Cancelar" da consulta marcada: o painel do
   * Atendimento (#1187). Ao terminar, `aoConcluir` em vez de voltar à lista.
   */
  comecarEm?: 'editar' | 'cancelar';
  /** Terminou o que abriu direto (`comecarEm`): o aviso, ou nulo se a pessoa desistiu. */
  aoConcluir?: (aviso: string | null) => void;
  /** Como a "Nova consulta" se arruma: uma etapa por vez no painel do Atendimento. */
  forma?: 'cartoes' | 'passos';
  /** O que vem antes do médico na etapa 1 (a escolha da oportunidade). */
  noComeco?: ReactNode;
  /** Depois de agendar, para quem abriu a aba direto no agendamento. */
  /** Agendou: o aviso, com o que a conferência disse (#1187). */
  aoAgendar?: (aviso?: string) => void;
  /** Cancelar quando a aba abriu direto no agendamento. */
  aoCancelarAgendamento?: () => void;
}): ReactNode {
  const consulta = useConsultaDaOportunidade(oportunidadeId);
  const clinica = usePreferencias().clinica;
  const operacoes = operacoesDaAgenda(clinica);
  const [modo, definirModo] = useState<Modo>(comecarEm ?? (comecarAgendando ? 'nova' : 'lista'));
  const [aviso, definirAviso] = useState<string | null>(null);

  if (consulta.isPending) {
    return <Carregando forma="ficha" linhas={3} rotulo="Carregando a consulta" />;
  }
  if (consulta.isError) {
    return (
      <Erro
        erro={consulta.error}
        titulo="Não foi possível abrir a consulta"
        aoRepetir={() => void consulta.refetch()}
      />
    );
  }

  const dados = consulta.data;
  const ir = (proximo: Modo, novoAviso: string | null = null) => {
    // Aberta direto num "Editar" ou "Cancelar" (o painel do Atendimento), quem abriu fecha.
    if (comecarEm && aoConcluir && proximo === 'lista') {
      aoConcluir(novoAviso);
      return;
    }
    definirAviso(novoAviso);
    definirModo(proximo);
  };

  const [item] = consultasNaLista(dados);
  /*
   * O "Editar" da consulta marcada, com a parte "Atualizar agendamento" ligada
   * (#1187): as mesmas 3 etapas do agendar, com o que foi gravado; salvar
   * atualiza o SISAC. Sem a parte, a edição fica só no CRM, como antes.
   */
  if (
    modo === 'editar' &&
    podeEditar &&
    item?.marcada &&
    item.codigoDoPaciente !== null &&
    operacoes?.remarcar
  ) {
    return (
      <MarcarConsulta
        oportunidadeId={oportunidadeId}
        consulta={dados}
        operacoes={operacoes}
        forma={forma}
        noComeco={null}
        telefoneDoContato={telefoneParaOSisac(contato?.telefone ?? null)}
        buscaDoPaciente={null}
        aoMarcar={(conferencia) => ir('lista', avisoDaEscrita('atualizada', conferencia))}
        aoCancelar={() => ir('lista')}
      />
    );
  }
  if (modo === 'editar' && podeEditar && item) {
    return (
      <EdicaoDaConsulta
        oportunidadeId={oportunidadeId}
        consulta={dados}
        item={item}
        aoSalvar={() => ir('lista', 'Consulta salva.')}
        aoFechar={() => ir('lista')}
      />
    );
  }
  const liberar = clinica['liberar_vaga'];
  if (modo === 'cancelar' && podeEditar && item?.marcada && liberar) {
    return (
      <CancelamentoDaConsulta
        oportunidadeId={oportunidadeId}
        consulta={dados}
        item={item}
        operacoes={{
          liberar,
          ...(clinica['motivos_de_cancelamento']
            ? { motivos: clinica['motivos_de_cancelamento'] }
            : {}),
        }}
        aoCancelar={(conferencia) => ir('lista', avisoDaEscrita('cancelada', conferencia))}
        aoVoltar={() => ir('lista')}
      />
    );
  }
  if (modo === 'nova' && podeEditar && operacoes) {
    return (
      <MarcarConsulta
        oportunidadeId={oportunidadeId}
        consulta={dados}
        operacoes={operacoes}
        forma={forma}
        noComeco={noComeco}
        telefoneDoContato={telefoneParaOSisac(contato?.telefone ?? null)}
        buscaDoPaciente={
          dados.cliente_id && pacienteNoSisacLigado(clinica) ? (
            <PacienteNoSisac
              key={dados.cliente_id}
              clienteId={dados.cliente_id}
              contato={contato ?? { nome: null, telefone: null, email: null }}
              podeEditar
            />
          ) : null
        }
        aoMarcar={(conferencia) => {
          const aviso = avisoDaEscrita('agendada', conferencia);
          ir('lista', aviso);
          aoAgendar?.(aviso);
        }}
        aoCancelar={() => {
          ir('lista');
          aoCancelarAgendamento?.();
        }}
      />
    );
  }

  return (
    <ListaDeConsultas
      consultas={item ? [item] : []}
      podeEditar={podeEditar}
      agendaConfigurada={operacoes !== null}
      aviso={aviso}
      destaqueId={aviso ? ID_DA_CONSULTA : null}
      aoNovaConsulta={() => ir('nova')}
      aoEditar={() => ir('editar')}
      {...(liberar ? { aoCancelar: () => ir('cancelar') } : {})}
    />
  );
}

/** "Cancelar" ligado ao `POST /v1/opportunities/{id}/consulta/cancelar` (Fase 3). */
function CancelamentoDaConsulta({
  oportunidadeId,
  consulta,
  item,
  operacoes,
  aoCancelar,
  aoVoltar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  item: ConsultaNaLista;
  operacoes: { readonly liberar: string; readonly motivos?: string };
  aoCancelar: (conferencia?: ConsultaDaOportunidade['conferencia']) => void;
  aoVoltar: () => void;
}): ReactNode {
  const cache = useQueryClient();
  const ligacao = useCancelamento({ oportunidadeId, consulta, operacoes, aoCancelar });
  return (
    <CancelarConsulta
      consulta={item}
      paciente={ligacao.paciente}
      motivos={ligacao.motivos}
      envio={ligacao.envio}
      aoCancelar={ligacao.cancelar}
      aoVoltar={aoVoltar}
      aoRecarregar={() => {
        void cache.invalidateQueries({ queryKey: chaves.consultaDaOportunidade(oportunidadeId) });
        aoVoltar();
      }}
    />
  );
}

/** "Editar" ligado ao `PUT /v1/opportunities/{id}/consulta`. */
function EdicaoDaConsulta({
  oportunidadeId,
  consulta,
  item,
  aoSalvar,
  aoFechar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  item: ConsultaNaLista;
  aoSalvar: () => void;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const opcoes = useOpcoesDaConsulta(true);

  const salvar = useMutation({
    mutationFn: async (edicao: EdicaoDaConsulta) => {
      if (!api) throw new Error('Sem sessão.');
      return api.salvarConsultaDaOportunidade({
        caminho: { id: oportunidadeId },
        corpo: {
          medico_id: edicao.medicoId === '' ? null : edicao.medicoId,
          especialidade_id: edicao.especialidadeId === '' ? null : edicao.especialidadeId,
          // Marcada, o horário é o do SISAC: volta o que já estava.
          consulta_em:
            edicao.horario === undefined ? consulta.consulta_em : doCampo(edicao.horario),
          versao: consulta.versao,
        },
      });
    },
    onSuccess: (salva) => {
      cache.setQueryData(chaves.consultaDaOportunidade(oportunidadeId), salva);
      aoSalvar();
    },
  });

  if (opcoes.isPending) {
    return <Carregando forma="ficha" linhas={3} rotulo="Carregando as opções" />;
  }
  if (opcoes.isError) {
    return (
      <Erro
        erro={opcoes.error}
        titulo="Não foi possível carregar os médicos e as especialidades"
        aoRepetir={() => void opcoes.refetch()}
      />
    );
  }

  const envio: EstadoDoEnvio = salvar.isPending
    ? { situacao: 'enviando' }
    : !salvar.isError
      ? { situacao: 'parado' }
      : salvar.error instanceof ErroDaApi && salvar.error.codigo === 'CONFLITO_DE_VERSAO'
        ? { situacao: 'conflito' }
        : {
            situacao: 'erro',
            recado: recadoDeErro(salvar.error, { generica: 'Não foi possível salvar a consulta.' }),
          };

  return (
    <EditarConsulta
      consulta={item}
      especialidades={opcoes.data.especialidades}
      medicos={opcoes.data.medicos}
      {...(consultaMarcada(consulta) ? {} : { horario: paraCampo(consulta.consulta_em) })}
      envio={envio}
      aoSalvar={(edicao) => salvar.mutate(edicao)}
      aoCancelar={aoFechar}
      aoRecarregar={() => {
        void cache.invalidateQueries({ queryKey: chaves.consultaDaOportunidade(oportunidadeId) });
        aoFechar();
      }}
    />
  );
}

/** "Nova consulta": espera as opções, porque é delas que sai o código do médico no SISAC. */
function MarcarConsulta({
  oportunidadeId,
  consulta,
  operacoes,
  forma,
  noComeco,
  telefoneDoContato,
  buscaDoPaciente,
  aoMarcar,
  aoCancelar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  operacoes: OperacoesDaAgenda;
  forma: 'cartoes' | 'passos' | undefined;
  noComeco: ReactNode;
  telefoneDoContato: string;
  buscaDoPaciente: ReactNode;
  aoMarcar: (conferencia?: ConsultaDaOportunidade['conferencia']) => void;
  aoCancelar: () => void;
}): ReactNode {
  const opcoes = useOpcoesDaConsulta(true);
  if (opcoes.isPending) {
    return (
      <Carregando forma="ficha" linhas={3} rotulo="Carregando os médicos e as especialidades" />
    );
  }
  if (opcoes.isError) {
    return (
      <Erro
        erro={opcoes.error}
        titulo="Não foi possível carregar os médicos e as especialidades"
        aoRepetir={() => void opcoes.refetch()}
      />
    );
  }
  return (
    <MarcarComAsOpcoes
      oportunidadeId={oportunidadeId}
      consulta={consulta}
      operacoes={operacoes}
      opcoes={{
        medicos: opcoesComAtual(opcoes.data.medicos, consulta.medico),
        especialidades: opcoesComAtual(opcoes.data.especialidades, consulta.especialidade),
      }}
      forma={forma}
      noComeco={noComeco}
      telefoneDoContato={telefoneDoContato}
      buscaDoPaciente={buscaDoPaciente}
      aoMarcar={aoMarcar}
      aoCancelar={aoCancelar}
    />
  );
}

function MarcarComAsOpcoes({
  oportunidadeId,
  consulta,
  operacoes,
  opcoes,
  forma,
  noComeco,
  telefoneDoContato,
  buscaDoPaciente,
  aoMarcar,
  aoCancelar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  operacoes: OperacoesDaAgenda;
  opcoes: Parameters<typeof useNovaConsulta>[0]['opcoes'];
  forma: 'cartoes' | 'passos' | undefined;
  noComeco: ReactNode;
  telefoneDoContato: string;
  buscaDoPaciente: ReactNode;
  aoMarcar: (conferencia?: ConsultaDaOportunidade['conferencia']) => void;
  aoCancelar: () => void;
}): ReactNode {
  const ligacao = useNovaConsulta({ oportunidadeId, consulta, operacoes, opcoes, aoMarcar });
  // O "Editar" espera o que está gravado na vaga: é com isso que o formulário começa.
  if (ligacao.remarcacao?.carregando) {
    return <Carregando forma="ficha" linhas={3} rotulo="Lendo o agendamento no SISAC" />;
  }
  // Os campos começam com o que a ficha já tem.
  const inicial: Partial<EscolhaDoMedico> = {
    ...(consulta.especialidade ? { especialidadeId: consulta.especialidade.id } : {}),
    ...(consulta.medico ? { medicoId: consulta.medico.id } : {}),
  };
  return (
    <NovaConsulta
      especialidades={opcoes.especialidades}
      medicos={opcoes.medicos}
      inicial={inicial}
      diasDeVagas={DIAS_DE_VAGAS}
      buscaDoPaciente={buscaDoPaciente}
      {...(forma ? { forma } : {})}
      noComeco={noComeco}
      telefoneDoContato={telefoneDoContato}
      aoCancelar={aoCancelar}
      {...ligacao}
    />
  );
}
